/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import * as Sentry from '@sentry/react';
import assign from 'lodash/assign';
import find from 'lodash/find';
import intersection from 'lodash/intersection';
import moment from 'moment';

import Config from 'Config';
import { PermissionType } from 'common/types/childClientManagement';
import {
    ACCOUNT_TYPE,
    CLIENT_STATUS,
    ROLES,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import * as logger from 'common/utils/Logger';
import Alert from 'common/components/Alert';
import { getContractorInvoiceAccess } from 'common/services/InvoiceService';
import { AUTH_RETURN_TO, PORTAL_TYPES } from 'common/utils/constants';
import { resetAllStores } from 'common/stores/redux/store';
import * as ApiService from 'common/services/ApiService';
import * as ClientService from 'common/services/ClientService';
import * as ContractorService from 'common/services/ContractorService';
import {
    update as updateAccount,
    patch as patchAccount,
    findById as getAuthenticatedAccount,
    createPreference,
    createAcknowledgement,
} from './AccountService';
import { getActiveContractorMSA } from './ContractorService';
import {
    clearCache as clearCacheFn,
    isLoggedIn as isLoggedInFn,
} from './LogoutService';
import { auth0Logout } from './Auth0LogoutService';

/**
 * login
 * @param {object} credentials
 * @returns {Promise}
 */
export async function login(credentials) {
    const _requestBody = assign({}, credentials, {
        portal: 'platform',
        realm: 'motili',
    });
    const response = await ApiService.post(
        false,
        'accounts/login',
        _requestBody,
        undefined,
        false
    );

    await _handleLoginResponse(response);
    if (!response.user.forcePasswordChange) {
        await _handleContractorLoginResponse();
    }
    return {
        token: JSON.parse(
            localStorage.getItem(`${Config.storageNameSpace}.token`)
        ),
        changePassword: response.user.forcePasswordChange,
    };
}

/**
 * Update local storage based on logged in users role
 * @param {object} response
 */
export async function _handleLoginResponse(response) {
    clearCache();
    localStorage.setItem(
        `${Config.storageNameSpace}.token`,
        JSON.stringify(response)
    );
    Sentry.setUser({
        id: response.user.id,
        email: response.user.email,
    });
    if (isClientAccount()) {
        const clientId = getClientId();
        if (clientId) {
            Sentry.setExtra({
                userContext: 'cli',
            });
            const client = await ClientService.findById(clientId, {
                filter: { include: ['address'] },
            });
            return localStorage.setItem(
                `${Config.storageNameSpace}.client`,
                JSON.stringify(client)
            );
        }
    } else if (isContractorAccount()) {
        const contractorId = getContractorId();
        if (contractorId) {
            Sentry.setExtra({
                userContext: 'con',
            });
            const contractor = await ContractorService.findById(contractorId, {
                filter: {
                    include: ['address', 'tradeTypes', 'clientPaymentAccount'],
                },
            });
            if (contractor.directSupply) {
                contractor.directSupplyClients =
                    await ContractorService.getDirectSupplyClients(
                        contractorId
                    );
            }
            try {
                const { sharedInvoice, selfInvoice } =
                    await getContractorInvoiceAccess(contractor.id);
                contractor.sharedInvoiceAccess = sharedInvoice;
                contractor.selfInvoiceAccess = selfInvoice;
            } catch (error) {
                logger.error('Error fetching contractor invoice access', error);
                Alert.error('Error fetching contractor invoice access');
            }

            // TODO JRo can add logic here for grabbing feature flags
            return localStorage.setItem(
                `${Config.storageNameSpace}.contractor`,
                JSON.stringify(contractor)
            );
        }
    } else {
        Sentry.setExtra({
            userContext: 'ops',
        });
    }
    return true;
}

/**
 * Update local storage if user is a contractor
 */
export async function _handleContractorLoginResponse() {
    if (isContractorAccount()) {
        const contractorMSA = await getActiveContractorMSA();
        const account = getAccount();
        const acknowledgment = find(account.acknowledgments, {
            type: contractorMSA.type,
            version: contractorMSA.version,
            acknowledged: true,
        });
        localStorage.setItem(
            `${Config.storageNameSpace}.contractorMSA`,
            JSON.stringify(
                assign(
                    {
                        type: contractorMSA.type,
                        version: contractorMSA.version,
                        acknowledged: false,
                    },
                    {
                        acknowledged:
                            acknowledgment && acknowledgment.acknowledged,
                    }
                )
            )
        );
    }
    return true;
}

/**
 *
 * @param {*} email
 */
export async function forgotPassword({ email }) {
    const _requestBody = {
        email,
        realm: 'motili',
    };
    return ApiService.post(false, 'accounts/forgotPassword', _requestBody);
}

/**
 *
 * @param {*} resetPasswordData
 */
export async function resetPassword(resetPasswordData) {
    const _requestBody = assign(
        {},
        {
            realm: 'motili',
            portal: 'platform',
            password: resetPasswordData.password,
            confirmationPassword: resetPasswordData.confirmationPassword,
            token: resetPasswordData.token,
        }
    );
    return ApiService.post(
        false,
        'accounts/updatePasswordWithToken',
        _requestBody
    ).catch(errObject => {
        throw errObject;
    });
}

/**
 *
 * @param {*} updatePasswordData
 */
export async function updatePassword(updatePasswordData) {
    const _requestBody = assign(
        {},
        {
            realm: 'motili',
            portal: 'platform',
            password: updatePasswordData.password,
            confirmationPassword: updatePasswordData.confirmationPassword,
            token: updatePasswordData.token,
        }
    );
    return ApiService.post(
        false,
        'accounts/updatePasswordWithEmail',
        _requestBody
    ).catch(errObject => {
        throw errObject;
    });
}

/**
 * Logout
 */
export const logout = auth0Logout;

/**
 * Check to see if a session exists
 * @returns {boolean}
 */
export const isLoggedIn = isLoggedInFn;

/**
 * Get the currently logged in account token
 * @returns {object}
 */
export function getToken() {
    return JSON.parse(localStorage.getItem(`${Config.storageNameSpace}.token`));
}

/**
 * Get the currently logged in account
 */
export function getAccount() {
    return JSON.parse(localStorage.getItem(`${Config.storageNameSpace}.token`))
        ?.user;
}

/**
 * Get the currently logged in account's roles
 * @return {Array}
 */
export function getAccountRoles() {
    if (!localStorage.getItem(`${Config.storageNameSpace}.token`)) return [];
    const _account = JSON.parse(
        localStorage.getItem(`${Config.storageNameSpace}.token`)
    )?.user;
    if (!_account) return [];
    const _roles = [];
    const cachedRoles = _account.roles || _account.motiliRoles; // temporary fix for people who have motiliRoles cached in local storage
    for (let i = 0; i < cachedRoles.length; i += 1) {
        _roles.push(cachedRoles[i].name);
    }
    return _roles;
}

export function getUserBaseRoute() {
    const token = JSON.parse(
        localStorage.getItem(`${Config.storageNameSpace}.token`)
    );
    switch (token?.user?.userType) {
        case ACCOUNT_TYPE.motili:
            return Config.routing.opsBaseRoute;
        case ACCOUNT_TYPE.client:
            return Config.routing.cliBaseRoute;
        case ACCOUNT_TYPE.contractor:
            return Config.routing.conBaseRoute;
        default:
    }
}

/**
 * Returns true if the account has account write access
 * @returns {boolean}
 */
export function hasAccountWriteAccess() {
    return isMotiliOwnerAndEngineeringDept();
}

/**
 * Returns true if the account has vendor write access
 * @returns {boolean}
 */
export function hasVendorWriteAccess() {
    return isMotiliOwnerAndEngineeringDept();
}

/**
 * Returns true if the account is a motili account
 * @returns {boolean}
 */
export function isMotiliAccount() {
    const roles = [
        'motili_owner',
        'motili_admin',
        'motili_manager',
        'motili_user',
    ];
    return hasRole(roles);
}

/**
 * Returns true if the account is a motili manager or above
 * @returns {boolean}
 */
export function isMotiliManager() {
    const roles = ['motili_owner', 'motili_admin', 'motili_manager'];
    return hasRole(roles);
}

/**
 * Returns true if the account is a motili owner and is a member of the motili engineering department
 * @returns {boolean}
 */
export function isMotiliOwnerAndEngineeringDept() {
    return isMotiliOwner() && isMotiliEngineeringDept();
}

/**
 * Returns true if the account is a motili admin account
 * @returns {boolean}
 */
export function isMotiliAdmin() {
    const roles = ['motili_owner', 'motili_admin'];
    return hasRole(roles);
}

/**
 * Returns true if the account is a motili admin account
 * @returns {boolean}
 */
export function isMotiliOwner() {
    const roles = ['motili_owner'];
    return hasRole(roles);
}

/**
 * Returns true if the account is a motili owner account with ap_finance department
 * @returns {boolean}
 */
export function isMotiliApFinanceDept() {
    if (!isMotiliOwner()) return false;
    const departments = getAccount().department || [];
    return !!find(departments, d => d === 'AP Finance');
}

/**
 * Returns true if the account is a motili account with finance department
 * @returns {boolean}
 */
export function isMotiliFinanceDept() {
    if (!isMotiliAccount()) return false;
    const departments = getAccount().department || [];
    return !!find(departments, d => d === 'Finance');
}

/**
 * Returns true if the account is a motili account with project management department
 * @returns {boolean}
 */
export function isMotiliProjectManager() {
    if (!isMotiliAccount()) return false;
    const departments = getAccount().department || [];
    return !!find(departments, d => d === 'Project Management');
}

/**
 * Returns true if the account is a motili account with contractor relations department
 * @returns {boolean}
 */
export function isMotiliContractorRelations() {
    if (!isMotiliAccount()) return false;
    const departments = getAccount().department || [];
    return !!find(departments, d => d === 'Contractor Relations');
}
/**
 * Returns true if the account is a motili account with engineering department
 * @returns {boolean}
 */
export function isMotiliEngineeringDept() {
    if (!isMotiliAccount()) return false;
    const departments = getAccount().department || [];
    return !!find(departments, d => d === 'Engineering');
}

/**
 * Returns true if the account is a motili owner account with Engineering OR Finance OR Contractor Relations department
 * @returns {boolean}
 */
export function isMotiliOwnerInDispatchingDept() {
    if (!isMotiliOwner()) return false;
    const departments = getAccount().department || [];
    return !!find(
        departments,
        d =>
            d === 'Engineering' ||
            d === 'Finance' ||
            d === 'Contractor Relations'
    );
}

/**
 * Returns true if the account is a client account
 * @returns {boolean}
 */
export function isClientAccount() {
    const roles = [
        'client_owner',
        'client_admin',
        'client_manager',
        'client_user',
    ];
    return hasRole(roles);
}

/**
 * Returns true if client only has read only access
 * @returns {boolean}
 */
export function isClientReadOnly() {
    const roles = ['client_user'];
    return hasRole(roles);
}

/**
 * Returns true if client is in a status that does not allow setting to be updated
 * @returns {boolean}
 */
export function isClientSettingUpdateRestricted(client) {
    const updatableStatuses = [CLIENT_STATUS.APPROVED, CLIENT_STATUS.ACTIVE];

    return !updatableStatuses.includes(client.statusId);
}

/**
 * Returns true if client account is different from document client
 * @returns {boolean}
 */
export function isDifferentClientThanAccount(clientId) {
    if (!clientId || !isClientAccount()) return false;

    const accountClientId = getClientId();
    return clientId !== accountClientId;
}

/**
 * Returns true if user has read only access for child client of current order
 * @returns {boolean}
 */
export function isChildClientReadOnly(clientId) {
    if (!clientId) return false;

    const account = getAccount();
    return (
        account?.childClientPermissions?.find(
            permission => permission.clientId === clientId
        )?.permissionTypeId === PermissionType.READ_ONLY
    );
}

/**
 * Returns true if client account is parent
 * @returns {boolean}
 */
export function isParentClientAccount(client) {
    return !client?.parentClientId;
}

/**
 * Returns true if user can view child client management workflow for a parent client
 * @returns {boolean}
 */
export function canViewChildClientManagement(client) {
    return (
        isMotiliOwner() &&
        isMotiliEngineeringDept() &&
        isParentClientAccount(client)
    );
}

/**
 * Returns true if client account has child client access role
 * @returns {boolean}
 */
export function childClientAccessEnabled(account) {
    return account.roles.some(role => role.name === ROLES.CHILD_CLIENT_ACCESS);
}

/**
 * Returns an array of child client IDs that the client user has read or read/write permissions for
 * @returns {number[]}
 */
export function getChildClientReadPermissionIds(account) {
    if (!childClientAccessEnabled(account)) return [];
    const token = JSON.parse(
        localStorage.getItem(`${Config.storageNameSpace}.token`)
    );
    const childClientPermissions = token?.user?.childClientPermissions || [];
    return childClientPermissions
        .filter(
            permission =>
                permission.permissionTypeId === PermissionType.READ_ONLY ||
                permission.permissionTypeId === PermissionType.READ_WRITE
        )
        .map(permission => permission.clientId);
}

/**
 * Returns true if the account is a client account
 * @returns {boolean}
 */
export function isContractorAccount() {
    const roles = [
        'contractor_owner',
        'contractor_admin',
        'contractor_manager',
        'contractor_user',
    ];
    return hasRole(roles);
}

/**
 * Returns true if the account is a contractor admin account
 * @returns {boolean}
 */
export function isContractorAdmin() {
    const roles = ['contractor_owner', 'contractor_admin'];
    return hasRole(roles);
}

/**
 * Returns true if the account is a contractor owner account
 * @returns {boolean}
 */
export function isContractorOwner() {
    const roles = ['contractor_owner'];
    return hasRole(roles);
}

/**
 * hasRole - returns true if the account has the specified role(s)
 * @param {(string|string[])} role
 * @returns {boolean}
 */
export function hasRole(role) {
    if (Array.isArray(role)) {
        return intersection(role, getAccountRoles()).length > 0;
    }
    const idx = getAccountRoles().indexOf(role);
    return idx !== -1;
}

/**
 * Get the full name, formatted tied to the account
 * @returns {string}
 */
export function getAccountFullName() {
    const _account = JSON.parse(
        localStorage.getItem(`${Config.storageNameSpace}.token`)
    )?.user;
    return _account && _account.firstName && _account.lastName
        ? `${_account.firstName} ${_account.lastName}`
        : 'Anonymous';
}

/**
 * Get the account's user name
 * @return {*|String}
 */
export function getAccountUserName() {
    return JSON.parse(localStorage.getItem(`${Config.storageNameSpace}.token`))
        ?.user?.username;
}

/**
 * Get account's id
 * @return {number} id
 */
export function getAccountId() {
    return JSON.parse(localStorage.getItem(`${Config.storageNameSpace}.token`))
        ?.user?.id;
}

/**
 * get the account's clientId
 * @return {Number}
 */
export function getClientId() {
    const token = JSON.parse(
        localStorage.getItem(`${Config.storageNameSpace}.token`)
    );
    return (
        token?.user && token.user?.clientTeam && token.user.clientTeam?.clientId
    );
}

/**
 * get logged in account's associated client
 * @return {id: number, operationInCountry: string}
 */
export function getClient() {
    return JSON.parse(
        localStorage.getItem(`${Config.storageNameSpace}.client`)
    );
}

/**
 * get the accounts contractorId
 * @return {Number}
 */
export function getContractorId() {
    const token = JSON.parse(
        localStorage.getItem(`${Config.storageNameSpace}.token`)
    );
    return (
        token?.user &&
        token.user?.contractorTeam &&
        token.user.contractorTeam?.contractorId
    );
}

export function getContractorTeamMemberId() {
    const token = JSON.parse(
        localStorage.getItem(`${Config.storageNameSpace}.token`)
    );
    return (
        token?.user &&
        token.user?.contractorTeam &&
        token.user.contractorTeam?.userId
    );
}

/**
 * get the logged in account's associated contractor
 * @return {id: number, operationInCountry: string}
 */
export function getContractor() {
    return JSON.parse(
        localStorage.getItem(`${Config.storageNameSpace}.contractor`)
    );
}

/**
 * Is current logged in contractor account able to view invoice table
 * @returns {boolean}
 */
export function isContractorInvoiceEligible() {
    if (!isContractorAccount()) {
        return false;
    }
    try {
        const contractor = getContractor();
        return (
            (contractor?.sharedInvoiceAccess ||
                contractor?.selfInvoiceAccess) &&
            contractor?.clientPaymentAccount?.autoInvoiceEnable
        );
    } catch (error) {
        logger.error(
            'Error checking contractor invoice view eligibility',
            error
        );
        return false;
    }
}

/**
 * Is current logged in account able to use direct supply feature
 * @returns {boolean}
 */
export function isDirectSupplyEligible(contractor) {
    return !!(
        contractor.directSupply && contractor.directSupplyClients?.length
    );
}

/**
 * Is current logged in account able to use self ordering feature
 * @returns {boolean}
 */
export function isSelfOrderingEligible(contractor) {
    return !!(contractor.clientPaymentAccountId && contractor.selfOrdering);
}

/**
 * Is current logged in contractor account able access order workflow
 * @returns {boolean}
 */
export function isOrderEligible() {
    if (!isContractorAccount()) {
        return false;
    }

    try {
        const contractor = getContractor();

        return (
            isDirectSupplyEligible(contractor) ||
            isSelfOrderingEligible(contractor)
        );
    } catch (error) {
        return false;
    }
}

/**
 * Is current logged in account is dispatchable
 * @returns {boolean}
 */
export function isDispatchable() {
    if (!isContractorAccount()) {
        return false;
    }
    try {
        const contractor = getContractor();
        return !!contractor.dispatchable;
    } catch (error) {
        return false;
    }
}

/**
 * Is current logged in account able to see product search list
 * @returns {boolean}
 */
export function isProductSearchListEnable() {
    const contractor = getContractor();
    return !!contractor?.enableProductSearchList;
}

/**
 * Clear the account cache, by default clears the entire account cache unless optional cache name is provided
 * @param {string} cache the cache to clear (optional)
 * @returns {undefined}
 */
export const clearCache = clearCacheFn;

/**
 * Retrieve an item from the cache as-is or all cache items
 * @param cache
 */
export function getCache(cache) {
    if (cache) {
        return localStorage.getItem(`${Config.storageNameSpace}.${cache}`);
    }
    const _cache = [];
    _cache.push(localStorage.getItem(`${Config.storageNameSpace}.token`));

    const client = localStorage.getItem(`${Config.storageNameSpace}.client`);
    if (client) {
        _cache.push(client);
    }
    const contractor = localStorage.getItem(
        `${Config.storageNameSpace}.contractor`
    );
    if (contractor) {
        _cache.push(contractor);
    }
    return Promise.all(_cache);
}

/**
 *
 * @param {*} roles
 */
export function mostPrivilegedRole(roles) {
    const ownerRE = /_owner$/;
    const adminRE = /_admin$/;
    const managerRE = /_manager$/;
    const userRE = /_user$/;
    const ownerRoles = [];
    const adminRoles = [];
    const managerRoles = [];
    const userRoles = [];
    for (let i = 0; i < roles.length; i += 1) {
        if (ownerRE.test(roles[i].name)) {
            ownerRoles.push(roles[i]);
            break;
        }
        if (adminRE.test(roles[i].name)) {
            adminRoles.push(roles[i]);
            break;
        }
        if (managerRE.test(roles[i].name)) {
            managerRoles.push(roles[i]);
            break;
        }
        if (userRE.test(roles[i].name)) {
            userRoles.push(roles[i]);
            break;
        }
    }
    /**
     * @todo differentiate between these
     */
    return (
        ownerRoles[0] ||
        adminRoles[0] ||
        managerRoles[0] ||
        userRoles[0] ||
        new Error('No known roles specified')
    );
}

/**
 * Update the currently authenticated account
 * @param update
 * @returns {Promise}
 */
export function updateAuthenticatedAccount(update) {
    const _update = assign(
        {},
        {
            email: update.email,
            firstName: update.firstName,
            lastName: update.lastName,
            phone: update.phone,
            phoneCountry: update.phoneCountry,
            phoneIsMobile: update.phoneIsMobile,
            phoneExt: update.phoneExt,
            timezoneId: update.timezoneId,
            preferences: update.preferences,
            roles: update.roles,
            userType: update.userType,
        }
    );

    const _query = {
        filter: {
            include: [
                {
                    relation: 'preferences',
                    scope: {
                        fields: [
                            'id',
                            'type',
                            'value',
                            'valueOptions',
                            'notification',
                            'primaryAccount',
                            'notificationPreferences',
                        ],
                    },
                },
            ],
        },
    };

    const contractorTeamObj = {
        relation: 'contractorTeam',
        scope: {
            fields: ['id', 'contractorId', 'receivesNotifications'],
            include: [
                {
                    relation: 'contractor',
                    scope: {
                        fields: [
                            'id',
                            'name',
                            'receivesNotifications',
                            'ffScheduler',
                        ],
                    },
                },
            ],
        },
    };

    const clientTeamObj = {
        relation: 'clientTeam',
        scope: {
            fields: ['id', 'clientId'],
            include: [
                {
                    relation: 'client',
                    scope: {
                        fields: ['id', 'name'],
                    },
                },
            ],
        },
    };

    if (update.userType === ACCOUNT_TYPE.contractor) {
        _query.filter.include.push(contractorTeamObj);
    }
    if (update.userType === ACCOUNT_TYPE.client) {
        _query.filter.include.push(clientTeamObj);
    }

    return updateAccount(getToken().userId, _update).then(() =>
        getAuthenticatedAccount(getToken().userId, _query)
    );
}

export function patchAuthenticatedAccount(update) {
    const _update = assign(
        {},
        {
            notificationPreferences: update.notificationPreferences,
        }
    );

    const _query = {
        filter: {
            include: [
                {
                    relation: 'preferences',
                    scope: {
                        fields: [
                            'id',
                            'type',
                            'value',
                            'valueOptions',
                            'notification',
                            'primaryAccount',
                            'notificationPreferences',
                        ],
                    },
                },
            ],
        },
    };

    return patchAccount(getToken().userId, _update).then(() =>
        getAuthenticatedAccount(getToken().userId, _query)
    );
}

export function createNewPreferenceForAuthenticatedAccount(preference) {
    const _query = {
        filter: {
            include: [
                {
                    relation: 'preferences',
                    scope: {
                        fields: [
                            'id',
                            'type',
                            'value',
                            'valueOptions',
                            'notification',
                            'primaryAccount',
                            'notificationPreferences',
                        ],
                    },
                },
            ],
        },
    };
    return createPreference(getToken().userId, preference).then(() =>
        getAuthenticatedAccount(getToken().userId, _query)
    );
}

/**
 * Update the currently authenticated account in local cache
 * @param updatedAccount
 * @returns {Promise}
 */
export function updateCachedAuthenticatedAccount(updatedAccount) {
    const tokenCache = getToken();
    clearCache('token');

    // Note: need to align user info for both localStorage and auth redux storage
    tokenCache.user = {
        ...tokenCache.user,
        ...updatedAccount,
    };

    localStorage.setItem(
        `${Config.storageNameSpace}.token`,
        JSON.stringify(tokenCache)
    );

    return Promise.resolve({ updatedAccount, tokenCache });
}

/**
 * Return latest contractor MSA
 * @return {object} acknowledgment
 * @return {string} acknowledgment.type
 * @return {string} acknowledgment.version
 * @return {boolean} acknowledgment.acknowledged
 */
export function latestAcknowledgment() {
    const acknowledgment = localStorage.getItem(
        `${Config.storageNameSpace}.contractorMSA`
    );
    return JSON.parse(acknowledgment);
}

/**
 * Create acknowledgement for user
 * @param {number} accountId
 * @param {object} acknowledgement
 * @param {string} acknowledgement.type
 * @param {string} acknowledgement.version
 * @param {boolean} acknowledgement.acknowledged
 * @return {Promise}
 */
export function acknowledged(accountId, acknowledgement) {
    return createAcknowledgement(accountId, acknowledgement).then(
        _acknowledgement => {
            if (!acknowledgement.acknowledged) {
                clearCache();
                // Reset all stores to initial state
                resetAllStores();
                Sentry.setUser(null);
                return _acknowledgement;
            }
            localStorage.setItem(
                `${Config.storageNameSpace}.contractorMSA`,
                JSON.stringify(_acknowledgement)
            );
            return _acknowledgement;
        }
    );
}

export async function extendToken() {
    const token = JSON.parse(
        localStorage.getItem(`${Config.storageNameSpace}.token`)
    );

    if (token?.id) {
        const response = await ApiService.get(true, 'accounts/extendToken');
        const updatedToken = {
            ...token,
            created: response.created,
            ttl: response.ttl,
            lastCheckFromBackend: moment().valueOf(),
        };
        localStorage.setItem(
            `${Config.storageNameSpace}.token`,
            JSON.stringify(updatedToken)
        );

        return updatedToken;
    }

    return undefined;
}

export async function refreshToken() {
    const token = JSON.parse(
        localStorage.getItem(`${Config.storageNameSpace}.token`)
    );

    if (token?.id) {
        if (token?.lastCheckFromBackend) {
            const nextCheck = moment(token.lastCheckFromBackend).add(
                Config.tokenValidation.backendInterval,
                'minute'
            );
            if (
                nextCheck &&
                nextCheck.isValid() &&
                nextCheck.isAfter(moment())
            ) {
                return undefined;
            }
        }
        const response = await ApiService.get(true, 'accounts/refreshToken');
        const updatedToken = {
            ...token,
            created: response.created,
            ttl: response.ttl,
            lastCheckFromBackend: moment().valueOf(),
        };

        localStorage.setItem(
            `${Config.storageNameSpace}.token`,
            JSON.stringify(updatedToken)
        );

        return updatedToken;
    }

    return undefined;
}

export function getExpireIn() {
    const token = JSON.parse(
        localStorage.getItem(`${Config.storageNameSpace}.token`)
    );
    if (token?.created && token?.ttl) {
        const expireTime = moment.utc(token.created).add(token.ttl, 'second');
        const current = moment.utc();

        return expireTime.diff(current, 'seconds');
    }
    return 0;
}

export function getRedirectPath(baseRoute, redirect) {
    const isValidRoute =
        redirect.includes(baseRoute) ||
        redirect.includes(`${Config.routing.toolsBaseRoute}/`);
    return isValidRoute ? redirect.replace('?redirectTo=', '') : '/';
}

export function getUserPortalType() {
    const token = JSON.parse(
        localStorage.getItem(`${Config.storageNameSpace}.token`)
    );
    switch (token?.user?.userType) {
        case ACCOUNT_TYPE.motili:
            return PORTAL_TYPES.ADMIN;
        case ACCOUNT_TYPE.client:
            return PORTAL_TYPES.CLIENT;
        case ACCOUNT_TYPE.contractor:
            return PORTAL_TYPES.CONTRACTOR;
        default:
    }
}

export function isBusinessChannelEditable() {
    return isMotiliOwner() && isMotiliEngineeringDept();
}

export function configureSessionRedirect(redirectTo) {
    sessionStorage.setItem(
        AUTH_RETURN_TO,
        JSON.stringify({ path: redirectTo })
    );
}

export function fetchSessionRedirect() {
    const { path } = JSON.parse(sessionStorage.getItem(AUTH_RETURN_TO) ?? '{}');
    return path || '/';
}
